import { useEffect, useState, useCallback } from "react";
import { useKeycloak } from "@react-keycloak/web";
import Swal from "sweetalert2";

import { FaRegTrashAlt } from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";

import Loader from "../../../../Atoms/Loader";
import MemberAvatar from "../../../../Molecules/MemberAvatar";

import ProjectService from "../../../../../services/Projects/ProjectService";

export default function MembersList({ projectId, reloadKey }) {
    const { keycloak } = useKeycloak();

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const [hoveredMemberId, setHoveredMemberId] = useState(null);

    const styles = {
        divider: {
            borderTop: '1px solid #314158',
            margin: '20px 0',
            height: '1px',
        },
        memberRow: {
            padding: '8px 0px',
            margin: '8px 0',
            borderRadius: '8px',
            transition: 'all 0.2s ease-in-out',
        },
        memberRowHover: {
            backgroundColor: '#1A1D29',
        },
        removeButton: {
            backgroundColor: 'transparent',
            border: 'none',
            color: '#ef4444',
            cursor: 'pointer',
            padding: '6px 10px',
            borderRadius: '6px',
        },
        refreshButton: {
            backgroundColor: 'transparent',
            border: '1px solid #314158',
            color: '#fff',
            cursor: 'pointer',
            padding: '6px 12px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
        },
    };

    const loadMembers = useCallback(async () => {
        if (!projectId || !keycloak.token) return;
        try {
            setLoading(true);
            const data = await ProjectService.getProjectMembers(projectId, keycloak.token);
            setMembers(Array.isArray(data) ? data : []);
        } catch (error) {
            Swal.fire({
                position: "top-end",
                title: "Erro ao carregar membros do projeto",
                showConfirmButton: false,
                timer: 1800,
                background: '#27272A',
                color: '#fff',
            });
            setMembers([]);
        } finally {
            setLoading(false);
        }
    }, [projectId, keycloak.token]);

    useEffect(() => {
        loadMembers();
    }, [loadMembers, reloadKey]);

    const handleRemove = async (member) => {
        const result = await Swal.fire({
            title: "Remover membro?",
            text: `Deseja remover ${member.username || member.email} deste projeto?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sim, remover",
            cancelButtonText: "Cancelar",
            background: '#27272A',
            color: '#fff',
        });

        if (!result.isConfirmed) return;

        try {
            setRemovingId(member.id);
            await ProjectService.removeProjectMember(projectId, member.email, keycloak.token);

            Swal.fire({
                position: "top-end",
                title: "Membro removido com sucesso",
                showConfirmButton: false,
                timer: 1800,
                background: '#27272A',
                color: '#fff',
            });

            await loadMembers();
        } catch (error) {
            Swal.fire({
                icon: "error",
                position: "top-end",
                title: error?.message || "Erro ao remover membro",
                showConfirmButton: false,
                timer: 1800,
                background: '#27272A',
                color: '#fff',
            });
        } finally {
            setRemovingId(null);
        }
    };

    return (
        <div>
            <div className="d-flex flex-row align-items-center justify-content-between mb-3">
                <p className="text-secondary mb-0">Membros associados ao projeto.</p>

                <button
                    type="button"
                    title="Recarregar lista de membros"
                    style={styles.refreshButton}
                    onClick={loadMembers}
                    disabled={loading}
                >
                    <FiRefreshCw size={14} className={loading ? 'rotating' : ''} />
                    Recarregar
                </button>
            </div>

            <div style={styles.divider}></div>

            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '120px' }}>
                    <Loader />
                </div>
            ) : (
                <>
                    {members.length === 0 && (
                        <p className="text-secondary">Nenhum membro associado a este projeto</p>
                    )}

                    <div className="list-scroll-items">
                        {members.map((member) => (
                            <div
                                key={member.id}
                                className="row align-items-center"
                                style={{
                                    ...styles.memberRow,
                                    ...(hoveredMemberId === member.id ? styles.memberRowHover : {}),
                                }}
                                onMouseEnter={() => setHoveredMemberId(member.id)}
                                onMouseLeave={() => setHoveredMemberId(null)}
                            >
                                <div className="col-12 col-md-8">
                                    <div className="d-flex flex-row gap-2 align-items-center">
                                        <MemberAvatar />
                                        <div className="d-flex flex-column">
                                            <p className="mb-0">{member.username || member.email}</p>
                                            {member.email && member.username && (
                                                <p className="text-secondary mb-0" style={{ fontSize: '0.85rem' }}>{member.email}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 col-md-4 d-flex justify-content-end">
                                    <button
                                        type="button"
                                        title="Remover membro do projeto"
                                        style={styles.removeButton}
                                        onClick={() => handleRemove(member)}
                                        disabled={removingId === member.id}
                                    >
                                        {removingId === member.id ? (
                                            <Loader />
                                        ) : (
                                            <FaRegTrashAlt size={16} />
                                        )}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
